import React, { Suspense, useMemo, Component, ErrorInfo, ReactNode, useEffect } from 'react';
import { MDXProvider } from '@mdx-js/react';
import { useLanguage } from '../../functions/useLanguage';
import { mdxComponents } from '../../components/guide/MDXComponents';
import { useBeanActions } from '../../hooks/useBeanActions';

// Import all MDX content statically (Vite requires static imports for proper bundling)
import WellbeingEn from '../../content/guide/en/wellbeing.mdx';
import WellbeingFr from '../../content/guide/fr/wellbeing.mdx';
import WellbeingDe from '../../content/guide/de/wellbeing.mdx';
import WellbeingPt from '../../content/guide/pt/wellbeing.mdx';
import WellbeingEs from '../../content/guide/es/wellbeing.mdx';

// Error boundary to catch MDX rendering errors
class ErrorBoundary extends Component<{ children: ReactNode; message?: string }, { hasError: boolean; error: Error | null }> {
  constructor(props: { children: ReactNode; message?: string }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('MDX Render Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-4 bg-error-red\/5 border border-error-red\/20 rounded-lg">
          <p className="text-error-red font-bold">{this.props.message ?? 'Error rendering content:'}</p>
          <pre className="text-error-red text-sm mt-2">{this.state.error?.message}</pre>
        </div>
      );
    }
    return this.props.children;
  }
}

const contentByLanguage: Record<string, React.ComponentType> = {
  English: WellbeingEn,
  French: WellbeingFr,
  German: WellbeingDe,
  Portuguese: WellbeingPt,
  Spanish: WellbeingEs,
};

function LoadingPlaceholder() {
  return (
    <div className="w-full max-w-5xl mx-auto animate-pulse">
      <div className="h-4 bg-gray-200 rounded w-1/3 mb-4" />
      <div className="h-10 bg-gray-200 rounded w-2/3 mb-6" />
      <div className="h-4 bg-gray-200 rounded w-full mb-2" />
      <div className="h-4 bg-gray-200 rounded w-5/6 mb-2" />
      <div className="h-4 bg-gray-200 rounded w-4/6" />
    </div>
  );
}

export function Wellbeing() {
  const { language, t } = useLanguage();
  const { onGuideArticleOpened } = useBeanActions();
  useEffect(() => { onGuideArticleOpened(); }, []);

  // Get the content component for current language, fallback to English
  const MDXContent = useMemo(() => {
    return contentByLanguage[language] || contentByLanguage.English;
  }, [language]);

  if (!MDXContent) {
    return <div className="text-error-red">No content found for language: {language}</div>;
  }

  return (
    <article className="w-full max-w-5xl mx-auto">
      <MDXProvider components={mdxComponents}>
        <ErrorBoundary message={t('guide.renderError')}>
          <Suspense fallback={<LoadingPlaceholder />}>
            <MDXContent />
          </Suspense>
        </ErrorBoundary>
      </MDXProvider>
    </article>
  );
}
